import { useMemo, useState } from "react";

export function PageHero({ eyebrow, title, children }) {
  return (
    <section className="page-hero">
      <div className="container">
        <span className="lbl">{eyebrow}</span>
        <h1>{title}</h1>
        {children ? <div className="page-hero__lede">{children}</div> : null}
      </div>
    </section>
  );
}

export function Section({ eyebrow, title, children, compact = false, id }) {
  return (
    <section id={id} className={`section${compact ? " section--compact" : ""}`}>
      <div className="container">
        {title ? (
          <div className="section__head">
            <div>
              {eyebrow ? <span className="lbl">{eyebrow}</span> : null}
              <h2 className="section__title">{title}</h2>
            </div>
          </div>
        ) : null}
        {children}
      </div>
    </section>
  );
}

export function SplitSection({ id, children }) {
  return (
    <section id={id} className="section">
      <div className="container split">{children}</div>
    </section>
  );
}

export function CodeBlock({ children, label }) {
  return (
    <div className="code-frame">
      {label ? <div className="code-frame__label">{label}</div> : null}
      <pre><code>{typeof children === "string" ? children.trim() : children}</code></pre>
    </div>
  );
}

export function CopyButton({ text, children = "Copy", primary = false }) {
  const [state, setState] = useState("idle");

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
      window.setTimeout(() => setState("idle"), 1600);
    } catch {
      setState("failed");
      window.setTimeout(() => setState("idle"), 2200);
    }
  }

  return (
    <button type="button" className={`btn${primary ? " btn--primary" : ""}`} onClick={copy} aria-live="polite">
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : children}
    </button>
  );
}

export function ExternalAnchor({ href, children }) {
  const external = href.startsWith("http");
  return (
    <a
      className="link-inline"
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
    >
      {children}{external ? " ↗" : " →"}
    </a>
  );
}

export function SearchBox({ records, placeholder, label = "Search" }) {
  const [query, setQuery] = useState("");

  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (normalized.length < 2) {
      return [];
    }
    return records
      .filter((record) => {
        const haystack = [
          record.title,
          record.summary,
          record.resourceUri,
          record.status,
          record.severity,
          record.category,
          record.tags?.join(" "),
          record.good,
          record.bad,
          record.sideEffects?.join(" "),
          record.verification?.join(" "),
          record.checklist
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(normalized);
      })
      .slice(0, 6);
  }, [query, records]);

  return (
    <form
      className="global-search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        if (matches[0]) {
          window.location.assign(matches[0].url);
        }
      }}
    >
      <label htmlFor="global-search" className="lbl">{label}</label>
      <div className="search global-search__row">
        <span className="slash">/</span>
        <input
          id="global-search"
          type="search"
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {query.trim().length >= 2 ? (
        <div className="search-results" aria-live="polite">
          {matches.length ? (
            matches.map((record) => (
              <a key={record.id} href={record.url}>
                <strong>{record.title}</strong>
                <span>{record.summary}</span>
                <code>{record.resourceUri}</code>
              </a>
            ))
          ) : (
            <div className="search-results__empty">No packets found. Try a broader architecture risk.</div>
          )}
        </div>
      ) : null}
    </form>
  );
}
